// 問題文を段階的に表示するカスタムフック

import { useState, useEffect, useRef } from "react";

type UseProgressiveTextProps = {
  fullText: string;
  charInterval: number; // 1文字あたりの表示間隔（ms）
  isActive: boolean; // 表示を開始するかどうか
};

export function useProgressiveText({
  fullText,
  charInterval,
  isActive,
}: UseProgressiveTextProps) {
  const [displayedText, setDisplayedText] = useState("");
  const clientStartTimeRef = useRef<number>(0);

  useEffect(() => {
    // isActive が変わった時の処理
    if (isActive && !clientStartTimeRef.current) {
      // READING_QUESTIONに変わった瞬間をクライアント側の基準時刻として記録
      clientStartTimeRef.current = Date.now();
    } else if (!isActive) {
      // 状態が変わったらリセット
      clientStartTimeRef.current = 0;
      setDisplayedText(fullText);
      return;
    }
  }, [isActive, fullText]);

  useEffect(() => {
    if (!isActive || !fullText || !clientStartTimeRef.current) {
      return;
    }

    // クライアント側の基準時刻から経過時間を計算
    const updateDisplayedText = () => {
      const now = Date.now();
      const elapsed = now - clientStartTimeRef.current;

      // 経過時間から表示する文字数を計算
      const charsToShow = Math.floor(elapsed / charInterval) + 1;

      if (charsToShow >= fullText.length) {
        // 全て表示完了
        setDisplayedText(fullText);
      } else {
        setDisplayedText(fullText.substring(0, charsToShow));
      }
    };

    // 初回実行
    updateDisplayedText();

    // charInterval毎に更新
    const intervalId = setInterval(updateDisplayedText, charInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [fullText, charInterval, isActive]);

  // 完全に表示されたかどうか
  const isComplete = displayedText === fullText;

  return { displayedText, isComplete };
}
